import { useNavigate, getRouteApi } from "@tanstack/react-router";
import { AvisosTitulo } from "@/components/AvisosTitulo";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { brl, dataBR, hojeISO, num } from "@/lib/estoque";
import { mascararQuantidade, mascararValor } from "@/lib/modo-valores";
import { DataBR } from "@/components/DataBR";
import { PrintPortal } from "@/components/PrintPortal";
import { RelatorioInsumosDia } from "@/components/RelatorioInsumosDia";
import { Permissao } from "@/hooks/useNivel";
import { Button } from "@/components/ui/button";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  FileDown,
  Printer,
  Share2,
} from "lucide-react";

import { obterClienteDados } from "@/lib/obter-dados";

/** Valor monetário com duas casas decimais, já respeitando o modo do usuário. */
const Route = getRouteApi("/_authenticated/vendas-dia");

const dinheiro = (v: number) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    mascararValor(Number(v) || 0),
  );
/** Quantidade já respeitando o modo do usuário (zerada quando sem acesso). */
const qtde = (v: number, casas = 0) => num(mascararQuantidade(Number(v) || 0), casas);

const SEMANA = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

/** Retorna a data de ontem no formato ISO (YYYY-MM-DD). */
export function ontemISO() {
  const d = new Date(`${hojeISO()}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Descreve a data com o dia da semana e os prefixos Hoje/Ontem. */
function rotuloDia(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  const nome = SEMANA[d.getDay()] ?? "";
  const hoje = hojeISO();
  if (iso === hoje) return `Hoje, ${nome}`;
  if (iso === ontemISO()) return `Ontem, ${nome}`;
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

type Coluna = "nome" | "qtd" | "valor";
type ColunaInsumo = "nome" | "qtd" | "custo";

export function VendasDiaPage() {
  const { data } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ConteudoVendasDia
      data={data}
      onData={(novo) => void navigate({ to: "/vendas-dia", search: { data: novo }, replace: true })}
      onVoltar={() => void navigate({ to: "/vendas" })}
    />
  );
}

/** Conteúdo da tela de vendas do dia, reutilizável dentro de uma janela. */
export function ConteudoVendasDia({
  data,
  onData,
  onVoltar,
}: {
  data: string;
  onData: (novo: string) => void;
  onVoltar: () => void;
}) {
  const [ordem, setOrdem] = useState<{ col: Coluna; asc: boolean }>({ col: "qtd", asc: false });
  const [aberto, setAberto] = useState<string | null>(null);
  const [ordemInsumo, setOrdemInsumo] = useState<{ col: ColunaInsumo; asc: boolean }>({
    col: "qtd",
    asc: false,
  });
  const [insumoAberto, setInsumoAberto] = useState<string | null>(null);
  const [imprimindoVendas, setImprimindoVendas] = useState(false);

  /** Mostra apenas a listagem de vendas na impressão. */
  const imprimirVendas = () => {
    setImprimindoVendas(true);
    setTimeout(() => {
      window.print();
      setImprimindoVendas(false);
    }, 80);
  };

  const setData = (novo: string) => onData(novo);

  const { data: sabores = [] } = useQuery({
    queryKey: ["flavors", "resumo"],
    queryFn: async () => {
      const banco = await obterClienteDados();
      const { data: rows, error } = await (banco as any)
        .from("flavors")
        .select("id, nome, preco")
        .order("nome");
      if (error) throw error;
      return rows;
    },
  });

  const { data: vendas = [], isLoading } = useQuery({
    queryKey: ["sales-recentes", data],
    queryFn: async () => {
      const banco = await obterClienteDados();
      const { data: rows, error } = await (banco as any)
        .from("sales")
        .select("id, data, created_at, total, sale_items(quantidade, flavor_id)")
        .eq("data", data)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return rows;
    },
  });

  const { data: receitas = [] } = useQuery({
    queryKey: ["flavor-ingredients-todos"],
    queryFn: async () => {
      const banco = await obterClienteDados();
      const { data: rows, error } = await (banco as any)
        .from("flavor_ingredients")
        .select(
          "flavor_id, ingredient_id, quantidade, insumos(nome, sigla, unidadeTrabalho, custo_unitario)",
        );
      if (error) throw error;
      return rows as unknown as {
        flavor_id: string;
        ingredient_id: string;
        quantidade: number;
        insumos: {
          nome: string;
          sigla: string;
          unidadeTrabalho: string;
          custo_unitario: number;
        } | null;
      }[];
    },
  });

  const porSabor = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const v of vendas)
      for (const it of v.sale_items ?? [])
        mapa.set(it.flavor_id, (mapa.get(it.flavor_id) ?? 0) + Number(it.quantidade));
    const lista = [...mapa.entries()].map(([id, qtd]) => {
      const s = sabores.find((x) => x.id === id);
      return {
        id,
        qtd,
        nome: s?.nome ?? "Produto removido",
        valor: qtd * Number(s?.preco ?? 0),
      };
    });
    const sinal = ordem.asc ? 1 : -1;
    return lista.sort((a, b) =>
      ordem.col === "nome"
        ? a.nome.localeCompare(b.nome, "pt-BR") * sinal
        : (a[ordem.col] - b[ordem.col]) * sinal,
    );
  }, [vendas, sabores, ordem]);

  /** Soma o consumo de cada insumo a partir das receitas dos produtos vendidos. */
  const porInsumo = useMemo(() => {
    const mapa = new Map<
      string,
      {
        id: string;
        nome: string;
        unidade: string;
        qtd: number;
        custo: number;
        produtos: { nome: string; qtd: number; custo: number }[];
      }
    >();
    for (const s of porSabor) {
      for (const r of receitas.filter((x) => x.flavor_id === s.id)) {
        const qtd = Number(r.quantidade) * s.qtd;
        const custo = qtd * Number(r.insumos?.custo_unitario ?? 0);
        const atual = mapa.get(r.ingredient_id) ?? {
          id: r.ingredient_id,
          nome: r.insumos?.nome ?? "Insumo removido",
          unidade: r.insumos?.unidadeTrabalho ?? "",
          qtd: 0,
          custo: 0,
          produtos: [],
        };
        atual.qtd += qtd;
        atual.custo += custo;
        atual.produtos.push({ nome: s.nome, qtd, custo });
        mapa.set(r.ingredient_id, atual);
      }
    }
    const sinal = ordemInsumo.asc ? 1 : -1;
    const lista = [...mapa.values()];
    for (const i of lista) i.produtos.sort((a, b) => b.qtd - a.qtd);
    return lista.sort((a, b) =>
      ordemInsumo.col === "nome"
        ? a.nome.localeCompare(b.nome, "pt-BR") * sinal
        : (a[ordemInsumo.col] - b[ordemInsumo.col]) * sinal,
    );
  }, [porSabor, receitas, ordemInsumo]);

  const custoTotalInsumos = porInsumo.reduce((a, i) => a + i.custo, 0);

  const faturamento = porSabor.reduce((a, s) => a + s.valor, 0);
  const totalItens = porSabor.reduce((a, s) => a + s.qtd, 0);

  /** Monta o PDF com a listagem de vendas da data. */
  const montarPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text(`Vendas de ${dataBR(data)}`, 40, 40);
    autoTable(doc, {
      startY: 60,
      head: [["Produto", "Qtde", "Valor R$"]],
      body: porSabor.map((s) => [s.nome, qtde(s.qtd), dinheiro(s.valor)]),
      foot: [["Total", qtde(totalItens), dinheiro(faturamento)]],
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });
    return doc;
  };

  const nomeArquivo = `vendas-${data}.pdf`;

  const exportarPDF = () => {
    if (porSabor.length === 0) {
      toast.error("Nenhuma venda nesta data.");
      return;
    }
    montarPDF().save(nomeArquivo);
  };

  const compartilharPDF = async () => {
    if (porSabor.length === 0) {
      toast.error("Nenhuma venda nesta data.");
      return;
    }
    const arquivo = new File([montarPDF().output("blob")], nomeArquivo, {
      type: "application/pdf",
    });
    const nav = navigator as Navigator & { canShare?: (dados: ShareData) => boolean };
    if (nav.canShare?.({ files: [arquivo] })) {
      try {
        await navigator.share({ files: [arquivo], title: `Vendas de ${dataBR(data)}` });
        return;
      } catch {
        return;
      }
    }
    montarPDF().save(nomeArquivo);
    toast.info("Compartilhamento não disponível; o PDF foi baixado.");
  };

  function Cab({
    col,
    direita,
    children,
  }: {
    col: Coluna;
    direita?: boolean;
    children: ReactNode;
  }) {
    const ativo = ordem.col === col;
    return (
      <th className={`p-2 ${direita ? "text-right" : "text-left"}`}>
        <button
          type="button"
          className="inline-flex items-center gap-1 font-medium hover:underline"
          onClick={() =>
            setOrdem((o) => (o.col === col ? { col, asc: !o.asc } : { col, asc: col === "nome" }))
          }
        >
          {children}
          {ativo &&
            (ordem.asc ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5" />
            ))}
        </button>
      </th>
    );
  }

  function CabInsumo({
    col,
    direita,
    children,
  }: {
    col: ColunaInsumo;
    direita?: boolean;
    children: ReactNode;
  }) {
    const ativo = ordemInsumo.col === col;
    return (
      <th className={`p-2 ${direita ? "text-right" : "text-left"}`}>
        <button
          type="button"
          className="inline-flex items-center gap-1 font-medium hover:underline"
          onClick={() =>
            setOrdemInsumo((o) =>
              o.col === col ? { col, asc: !o.asc } : { col, asc: col === "nome" },
            )
          }
        >
          {children}
          {ativo &&
            (ordemInsumo.asc ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5" />
            ))}
        </button>
      </th>
    );
  }

  return (
    <Permissao nivelMaximo={3}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="icon" aria-label="Voltar" onClick={onVoltar}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-semibold">Vendas de {dataBR(data)}</h1>
          <AvisosTitulo />
          <Button
            variant="outline"
            size="icon"
            onClick={imprimirVendas}
            aria-label="Imprimir"
            title="Imprimir"
          >
            <Printer className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={exportarPDF}
            aria-label="Exportar PDF"
            title="Exportar PDF"
          >
            <FileDown className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => void compartilharPDF()}
            aria-label="Compartilhar"
            title="Compartilhar"
          >
            <Share2 className="h-4 w-4" />
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{rotuloDia(data)}</span>
            <DataBR id="data-vendas-dia" value={data} onChange={setData} />
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <Cab col="nome">Produto</Cab>
                <Cab col="qtd" direita>
                  Qtde
                </Cab>
                <Cab col="valor" direita>
                  Valor R$
                </Cab>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={3} className="p-3 text-muted-foreground">
                    Carregando…
                  </td>
                </tr>
              )}
              {!isLoading && porSabor.length === 0 && (
                <tr>
                  <td colSpan={3} className="p-3 text-muted-foreground">
                    Nenhuma venda nesta data.
                  </td>
                </tr>
              )}
              {porSabor.map((s, i) => {
                const itens = receitas.filter((r) => r.flavor_id === s.id);
                const expandido = aberto === s.id;
                return (
                  <Fragment key={s.id}>
                    <tr
                      className={`border-t border-border/60 ${i % 2 === 1 ? "bg-muted/30" : "bg-background"}`}
                    >
                      <td className="p-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-left hover:underline"
                          onClick={() => setAberto(expandido ? null : s.id)}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${expandido ? "" : "-rotate-90"}`}
                          />
                          {s.nome}
                        </button>
                      </td>
                      <td className="p-2 text-right">{qtde(s.qtd)}</td>
                      <td className="p-2 text-right font-medium">{dinheiro(s.valor)}</td>
                    </tr>
                    {expandido && (
                      <tr className={i % 2 === 1 ? "bg-muted/30" : "bg-background"}>
                        <td colSpan={3} className="px-6 pb-3 pt-0">
                          {itens.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              Sem receita cadastrada para este produto.
                            </p>
                          ) : (
                            <ul className="space-y-1 text-xs">
                              {itens.map((it, k) => (
                                <li key={k} className="flex justify-between gap-4">
                                  <span>
                                    {it.insumos?.nome ?? "Insumo removido"}
                                    {it.insumos?.sigla ? ` (${it.insumos.sigla})` : ""}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {qtde(Number(it.quantidade), 2)}{" "}
                                    {it.insumos?.unidadeTrabalho ?? ""} · total{" "}
                                    {qtde(Number(it.quantidade) * s.qtd, 2)}{" "}
                                    {it.insumos?.unidadeTrabalho ?? ""}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-right">
          Itens: <strong>{qtde(totalItens)}</strong> · Total: <strong>{brl(faturamento)}</strong>
        </p>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold">Insumos utilizados</h2>
            <RelatorioInsumosDia data={data} linhas={porInsumo} mostrarTabela={false} />
          </div>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <CabInsumo col="nome">Insumo</CabInsumo>
                  <CabInsumo col="qtd" direita>
                    Qtde
                  </CabInsumo>
                  <CabInsumo col="custo" direita>
                    Custo R$
                  </CabInsumo>
                </tr>
              </thead>
              <tbody>
                {porInsumo.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-3 text-muted-foreground">
                      Nenhum insumo consumido nesta data.
                    </td>
                  </tr>
                )}
                {porInsumo.map((ins, i) => {
                  const expandido = insumoAberto === ins.id;
                  return (
                    <Fragment key={ins.id}>
                      <tr
                        className={`border-t border-border/60 ${i % 2 === 1 ? "bg-muted/30" : "bg-background"}`}
                      >
                        <td className="p-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-left hover:underline"
                            onClick={() => setInsumoAberto(expandido ? null : ins.id)}
                          >
                            <ChevronDown
                              className={`h-4 w-4 transition-transform ${expandido ? "" : "-rotate-90"}`}
                            />
                            {ins.nome}
                          </button>
                        </td>
                        <td className="p-2 text-right">
                          {qtde(ins.qtd, 2)} {ins.unidade}
                        </td>
                        <td className="p-2 text-right font-medium">{dinheiro(ins.custo)}</td>
                      </tr>
                      {expandido && (
                        <tr className={i % 2 === 1 ? "bg-muted/30" : "bg-background"}>
                          <td colSpan={3} className="px-6 pb-3 pt-0">
                            <ul className="space-y-1 text-xs">
                              {ins.produtos.map((p, k) => (
                                <li key={k} className="flex gap-4">
                                  <span className="flex-1">{p.nome}</span>
                                  <span className="text-muted-foreground">
                                    {qtde(p.qtd, 2)} {ins.unidade}
                                  </span>
                                  <span className="w-[70px] text-right text-muted-foreground">
                                    R$ {dinheiro(p.custo)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {porInsumo.length > 0 && (
            <p className="text-sm">
              Custo total dos insumos: <strong>{brl(custoTotalInsumos)}</strong>
            </p>
          )}
        </section>
      </div>

      {imprimindoVendas && (
        <PrintPortal>
          <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            Vendas de {dataBR(data)} — {rotuloDia(data)}
          </h1>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #000", padding: 4 }}>
                  Produto
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}>
                  Qtde
                </th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}>
                  Valor R$
                </th>
              </tr>
            </thead>
            <tbody>
              {porSabor.map((s) => (
                <tr key={s.id}>
                  <td style={{ padding: 4, borderBottom: "1px solid #ccc" }}>{s.nome}</td>
                  <td style={{ padding: 4, borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    {qtde(s.qtd)}
                  </td>
                  <td style={{ padding: 4, borderBottom: "1px solid #ccc", textAlign: "right" }}>
                    {dinheiro(s.valor)}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: 4, fontWeight: 600 }}>Total</td>
                <td style={{ padding: 4, textAlign: "right", fontWeight: 600 }}>
                  {qtde(totalItens)}
                </td>
                <td style={{ padding: 4, textAlign: "right", fontWeight: 600 }}>
                  {dinheiro(faturamento)}
                </td>
              </tr>
            </tbody>
          </table>
        </PrintPortal>
      )}
    </Permissao>
  );
}

/** Nome deste arquivo, injetado em build (não editar). */
export const ARQUIVO = "vendas-dia.tsx";
